import {diagramsApi, type ErrorProcessor} from '@dv5/api'
import {ErrorDisplayProcessor} from "@dv5/components"
import {DiagramWidgetData, useAppDispatch} from '@dv5/domain'
import React, {FC, useEffect, useState} from 'react'
import './DiagramViewer.less'

interface Props {
    sourceCode: string
    diagramType: DiagramWidgetData['diagramType']
    hideType: boolean
}4

export const DiagramViewer: FC<Props> = ({sourceCode, diagramType, hideType}) => {
    const [renderedImage, setRenderedImage] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [renderError, setRenderError] = useState<string | null>(null)
    const dispatch = useAppDispatch()

    useEffect(() => {
        if (!sourceCode) {
            setRenderedImage(null)
            setIsLoading(false)
            setRenderError(null)
            return
        }

        let cancelled = false
        setIsLoading(true)
        setRenderError(null)

        const errorProcessor = new ErrorDisplayProcessor(dispatch)
        diagramsApi.render(diagramType, sourceCode, errorProcessor)
            .then(response => {
                if (cancelled) return
                if (response.success) {
                    setRenderedImage(`data:${response.contentType};base64,${response.data}`)
                    setRenderError(null)
                } else {
                    setRenderError(response.message || 'Failed to render diagram')
                    setRenderedImage(null)
                }
            })
            .catch(err => {
                if (cancelled) return
                setRenderError(typeof err === 'string' ? err : 'Failed to render diagram')
                setRenderedImage(null)
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [sourceCode, diagramType])

    return (
        <div className='diagram-viewer'>
            {!hideType && <div className='diagram-viewer__header'>
                <span className='diagram-viewer__type'>{diagramType}</span>
            </div>}
            {isLoading && !renderedImage && (
                <div className='diagram-viewer__loading'>Rendering...</div>
            )}
            {renderError && (
                <div className='diagram-viewer__error'>{renderError}</div>
            )}
            {renderedImage ? (
                <div className='diagram-viewer__image'>
                    <img src={renderedImage} alt={diagramType}/>
                </div>
            ) : (
                !isLoading && (
                    <pre className='diagram-viewer__code'>
                        <code>{sourceCode || '(empty)'}</code>
                    </pre>
                )
            )}
        </div>
    )
}
